import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, Loader2, Send, X } from "lucide-react";
import { API } from "service/Interceptor";
import { postProject } from "service/apiUrl";
import toast from "components/Toast/toast";
import DateField from "components/app/DateField";
import {
  buildCreatorBriefPostContent,
  buildCreatorBriefSnapshot,
  upsertCreatorBrief,
} from "utils/creatorBriefs";

const BUDGETS = ["Under ₹10k", "₹10k–25k", "₹25k–50k", "₹50k–1L", "₹1L+"];
const TIMELINES = ["ASAP", "Flexible"];
const WORK_STYLES = ["In-person", "Hybrid", "Remote"];
const INDUSTRIES = [
  "Fashion",
  "Beauty",
  "Jewellery",
  "Lifestyle",
  "Product",
  "Technology",
  "Media, Marketing, Publishing & PR",
  "Designing",
  "Entertainment",
  "Events & Weddings",
  "Hospitality",
  "Advertising",
  "Architecture",
  "Production",
  "Music",
  "Other",
];

const BUDGET_VALUES = {
  "Under ₹10k": 10000,
  "₹10k–25k": 25000,
  "₹25k–50k": 50000,
  "₹50k–1L": 100000,
  "₹1L+": 150000,
};

const label = "block text-sm text-zinc-300 mb-2 font-medium";
const field =
  "w-full rounded-xl bg-zinc-900/70 border border-zinc-800 text-zinc-100 placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:border-[#6C63F5] transition-colors";

const Pill = ({ active, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
      active
        ? "border-[#6C63F5] bg-[#6C63F5]/15 text-zinc-50"
        : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-600"
    }`}
  >
    {children}
  </button>
);

const matchByName = (list, name) => {
  if (!name || !Array.isArray(list)) return null;
  const needle = String(name).trim().toLowerCase();
  return (
    list.find((item) => String(item?.name || "").toLowerCase() === needle) ||
    list.find((item) =>
      String(item?.name || "").toLowerCase().includes(needle)
    ) ||
    null
  );
};

/** Profile/free-text locations often store "City, State" — keep only the city. */
const cityOnly = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.split(",")[0].trim();
};

const defaultBriefCity = (userProfile) => {
  const company = userProfile?.companyResponseDto;
  return cityOnly(
    company?.city?.name ||
      company?.cityName ||
      userProfile?.cityName ||
      ""
  );
};

/** Resolve location + category objects the jobs API requires. */
const resolveBrandJobRefs = async (userProfile, cityInput, industryName) => {
  const company = userProfile?.companyResponseDto;
  let stateId =
    userProfile?.stateId || company?.stateId || company?.state?.id || null;
  let cityId =
    userProfile?.cityId || company?.cityId || company?.city?.id || null;
  let stateName =
    userProfile?.stateName || company?.stateName || company?.state?.name || "";

  const rawCity = (cityInput || "").trim();
  let cityName = cityOnly(rawCity);
  // If user typed "City, State", prefer that state for lookup
  if (rawCity.includes(",")) {
    const fromInput = rawCity.split(",").slice(1).join(",").trim();
    if (fromInput) stateName = fromInput;
  }
  if (!cityName) {
    cityName = defaultBriefCity(userProfile);
  }

  const statesRes = await API.get("api/locations/states");
  const catPromise = API.get("api/categories");
  const states = statesRes?.data?.data?.results || [];

  // Prefer matching state by name when city input included a state part
  if (stateName) {
    const byStateName = matchByName(states, stateName);
    if (byStateName?.id) {
      stateId = byStateName.id;
      stateName = byStateName.name;
    }
  }

  if (!stateId) {
    const byName = matchByName(states, stateName) || states[0];
    stateId = byName?.id || null;
    stateName = byName?.name || stateName;
  } else {
    const found = states.find((s) => Number(s.id) === Number(stateId));
    if (found) stateName = found.name;
  }

  if (!stateId) {
    throw new Error("Could not resolve a state for this brief. Update your profile location and try again.");
  }

  const [catRes, citiesRes] = await Promise.all([
    catPromise,
    API.get(`api/locations/states/${stateId}/cities`),
  ]);
  const cities = citiesRes?.data?.data?.results || [];

  const typedCity = cityName;
  if (cityInput?.trim()) {
    const byName = matchByName(cities, typedCity);
    if (byName) {
      cityId = byName.id;
      cityName = byName.name;
    } else {
      // Keep labels like "All cities" — do not replace with a random first city.
      cityName = typedCity;
      if (!cityId && cities[0]) cityId = cities[0].id;
    }
  } else if (cityId) {
    const found = cities.find((c) => Number(c.id) === Number(cityId));
    if (found) cityName = found.name;
  } else if (cities[0]) {
    cityId = cities[0].id;
    cityName = typedCity || cities[0].name;
  }

  if (!cityId) {
    throw new Error("Could not resolve a city for this brief. Enter a valid city and try again.");
  }

  const categories = catRes?.data?.data?.results || [];
  const category =
    matchByName(categories, industryName) ||
    categories[0] ||
    null;

  if (!category?.id) {
    throw new Error("Could not resolve an industry category. Please try again.");
  }

  return {
    state: { id: Number(stateId), name: stateName || undefined },
    city: { id: Number(cityId), name: cityName || undefined },
    category: [{ id: Number(category.id), name: category.name }],
  };
};

const BriefModal = ({ open, onClose, onPosted }) => {
  const { userProfile } = useSelector((state) => state.userProfile);
  const isBrand = userProfile?.userType === "BUSINESS";

  const [need, setNeed] = useState("");
  const [industry, setIndustry] = useState("Fashion");
  const [customIndustry, setCustomIndustry] = useState("");
  const [creativeType, setCreativeType] = useState("");
  const [budget, setBudget] = useState("₹10k–25k");
  const [timeline, setTimeline] = useState("Flexible");
  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectEndDate, setProjectEndDate] = useState("");
  const [workStyle, setWorkStyle] = useState("In-person");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setNeed("");
    setIndustry("Fashion");
    setCustomIndustry("");
    setCreativeType("");
    setBudget("₹10k–25k");
    setTimeline("Flexible");
    setProjectStartDate("");
    setProjectEndDate("");
    setWorkStyle("In-person");
    setCity(defaultBriefCity(userProfile));
    setError("");
  }, [
    open,
    userProfile?.cityName,
    userProfile?.companyResponseDto?.cityName,
    userProfile?.companyResponseDto?.city?.name,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    if (!need.trim()) return;

    if (!projectStartDate || !projectEndDate) {
      setError("Please select project start and end dates");
      return;
    }

    const start = new Date(`${projectStartDate}T00:00:00`);
    const end = new Date(`${projectEndDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError("Please enter valid project dates");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date");
      return;
    }

    const resolvedIndustry =
      industry === "Other" ? customIndustry.trim() : industry;
    if (!resolvedIndustry) {
      setError("Please type the industry for this brief.");
      return;
    }

    setBusy(true);
    setError("");
    const finalType = creativeType.trim() || "Creative";
    const budgetValue = BUDGET_VALUES[budget] || 25000;
    const durationDays = Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );

    // Brands and creators both create a real Job so Apply uses JobApplication
    // (email, applicants, View details). A BRIEFS feed post announces it.
    try {
      const userKey =
        userProfile?.userAliasId ||
        userProfile?.id ||
        userProfile?.userId;
      const cityLabel = cityOnly(city) || defaultBriefCity(userProfile);
      const refs = await resolveBrandJobRefs(userProfile, cityLabel, resolvedIndustry);
      const payload = {
        jobTitle: need.trim().slice(0, 80),
        description: buildCreatorBriefPostContent({
          need,
          industry: resolvedIndustry,
          creativeType: finalType,
          workStyle,
          city: cityLabel || refs.city?.name,
          budgetValue,
          timeline,
          projectStartDate,
          projectEndDate,
        }),
        skillsRequired: [finalType],
        city: refs.city,
        state: refs.state,
        projectBudget: budgetValue,
        projectDuration: durationDays,
        noOfOpenings: 1,
        // Listing window (Open briefs requires start_date <= now). Project
        // schedule stays in the description above — not these fields.
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        category: refs.category,
        industry: [resolvedIndustry],
      };

      let jobId = null;
      try {
        const res = await API.post(postProject, payload, {
          skipErrorToast: !isBrand,
        });
        if (!(res?.status === 200 || res?.data?.success)) {
          throw new Error(res?.data?.message || "Could not post brief");
        }
        jobId = res?.data?.data?.jobId || res?.data?.data?.id || null;
      } catch (jobErr) {
        // QA/older APIs reject creator job create — fall back to feed post only
        // (Apply stays comment-based until backend supports creator jobs).
        if (isBrand) throw jobErr;
      }

      // Announce on the social feed (with jobId so Apply hits the jobs API).
      let postId = null;
      const postRes = await API.post("api/post", {
        tags: ["BRIEFS"],
        content: buildCreatorBriefPostContent({
          need,
          industry: resolvedIndustry,
          creativeType: finalType,
          workStyle,
          city: cityLabel || refs.city?.name,
          budgetValue,
          timeline,
          projectStartDate,
          projectEndDate,
          jobId,
        }),
        mediaIds: [],
      });
      if (!(postRes?.status === 200 || postRes?.data?.success)) {
        if (!jobId) {
          throw new Error(
            postRes?.data?.message || "Could not post brief"
          );
        }
      } else {
        postId = postRes?.data?.data?.id || null;
      }

      if (!isBrand) {
        const snapshot = buildCreatorBriefSnapshot({
          need,
          industry: resolvedIndustry,
          creativeType: finalType,
          workStyle,
          city: cityLabel || city.trim(),
          budgetValue,
          timeline,
          projectStartDate,
          projectEndDate,
          userProfile,
          postId,
          jobId,
        });
        upsertCreatorBrief(userKey, snapshot);
      }

      toast.success(
        isBrand ? "Brief posted" : "Brief posted — visible in Open briefs"
      );
      onPosted?.({ jobId, postId });
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.errors) &&
          err.response.data.errors.map((x) => x?.message || x).join(", ")) ||
        err?.message ||
        "Could not post brief";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#6C63F5]/15 flex items-center justify-center">
                  <Briefcase size={18} className="text-[#8b83ff]" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl tracking-tight text-zinc-50">
                    Post a brief
                  </h3>
                  <p className="text-xs text-zinc-500">
                    {isBrand
                      ? "Visible for 30 days — appears in the feed and Projects."
                      : "Appears under Briefs you've posted."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-zinc-500 hover:text-zinc-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className={label}>What work do you need done?</label>
                <textarea
                  required
                  rows={3}
                  value={need}
                  onChange={(e) => setNeed(e.target.value)}
                  placeholder="e.g. Need a logo & brand identity for a new coffee shop"
                  className={`${field} resize-none`}
                />
                <p className="mt-1.5 text-xs text-zinc-500">
                  Describe the project. Not your bio, and not that you need clients.
                </p>
              </div>

              <div>
                <label className={label}>Select industry</label>
                <select
                  value={industry}
                  onChange={(e) => {
                    setIndustry(e.target.value);
                    if (e.target.value !== "Other") setCustomIndustry("");
                  }}
                  className={field}
                >
                  {INDUSTRIES.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </select>
                {industry === "Other" && (
                  <input
                    value={customIndustry}
                    onChange={(e) => setCustomIndustry(e.target.value)}
                    placeholder="Type the industry"
                    className={`${field} mt-2`}
                    required
                  />
                )}
              </div>

              <div>
                <label className={label}>Who do you need?</label>
                <input
                  value={creativeType}
                  onChange={(e) => setCreativeType(e.target.value)}
                  placeholder="e.g. Photographer, Stylist, Cinematographer"
                  className={field}
                />
              </div>

              <div>
                <label className={label}>Work style</label>
                <div className="flex flex-wrap gap-2">
                  {WORK_STYLES.map((w) => (
                    <Pill
                      key={w}
                      active={workStyle === w}
                      onClick={() => setWorkStyle(w)}
                    >
                      {w}
                    </Pill>
                  ))}
                </div>
              </div>

              <div>
                <label className={label}>City</label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. Mumbai"
                  className={field}
                />
              </div>

              <div>
                <label className={label}>Budget range</label>
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map((b) => (
                    <Pill
                      key={b}
                      active={budget === b}
                      onClick={() => setBudget(b)}
                    >
                      {b}
                    </Pill>
                  ))}
                </div>
              </div>

              <div>
                <label className={label}>Project Date</label>
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3.5 relative overflow-visible">
                  <div
                    className="pointer-events-none absolute inset-0 opacity-40 rounded-2xl"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 60% at 0% 0%, rgba(108,99,245,0.18), transparent 55%)",
                    }}
                  />
                  <div className="relative grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                    <DateField
                      label="Start"
                      value={projectStartDate}
                      min={new Date().toISOString().slice(0, 10)}
                      placeholder="Pick start date"
                      onChange={(next) => {
                        setProjectStartDate(next);
                        if (projectEndDate && next && projectEndDate < next) {
                          setProjectEndDate(next);
                        }
                      }}
                    />
                    <div className="hidden sm:flex items-center justify-center pb-3 text-zinc-600">
                      <ArrowRight size={16} className="text-[#8b83ff]/70" />
                    </div>
                    <DateField
                      label="End"
                      value={projectEndDate}
                      min={
                        projectStartDate ||
                        new Date().toISOString().slice(0, 10)
                      }
                      placeholder="Pick end date"
                      onChange={setProjectEndDate}
                    />
                  </div>
                  {projectStartDate && projectEndDate && (
                    <p className="relative mt-3 text-xs text-zinc-500">
                      {projectStartDate === projectEndDate
                        ? "Single-day project"
                        : `Runs ${Math.max(
                            1,
                            Math.round(
                              (new Date(projectEndDate) -
                                new Date(projectStartDate)) /
                                (1000 * 60 * 60 * 24)
                            ) + 1
                          )} days`}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className={label}>Timeline</label>
                <div className="flex flex-wrap gap-2">
                  {TIMELINES.map((t) => (
                    <Pill
                      key={t}
                      active={timeline === t}
                      onClick={() => setTimeline(t)}
                    >
                      {t}
                    </Pill>
                  ))}
                </div>
              </div>

              {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !need.trim()}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#6C63F5] text-white hover:bg-[#5751E5] rounded-full px-6 py-3.5 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={15} />
                )}
                Post brief
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BriefModal;
